import styled from '@emotion/styled';
import { Typography, IconButton } from '@taleemabad/dsm/components';

export enum AppBarVariant {
  Primary = 'primary',
  StackedBack = 'stackedBack',
}

interface AppBarV3Props {
  title?: string;
  variant?: AppBarVariant;
  leadingIcon?: string;
  onLeadingIconClick?: () => void;
  onTitleClick?: () => void;
  actions?: Array<{
    icon: string;
    onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
  }>;
  actionComponent?: React.ReactNode;
  'data-cy'?: string;
}

export const Appbar = ({
  title,
  variant = AppBarVariant.Primary,
  leadingIcon,
  onLeadingIconClick,
  onTitleClick,
  actions = [],
  actionComponent,
  'data-cy': dataCy,
  ...otherProps
}: AppBarV3Props) => {
  const renderLeadingIcon = () =>
    leadingIcon ? (
      <IconButton
        data-cy="back-button"
        icon={leadingIcon}
        onClick={onLeadingIconClick}
      />
    ) : (
      <StyledIconPlaceholder />
    );

  const renderActions = () =>
    actions.length ? (
      <StyledActionIconsContainer>
        {actions.map((actionIcon, index) => (
          <IconButton
            key={actionIcon.icon}
            icon={actionIcon.icon}
            onClick={actionIcon.onClick}
            aria-label={`${actionIcon.icon} action`}
            data-cy={`appbar-action-${actionIcon.icon}`}
            {...otherProps}
          />
        ))}
      </StyledActionIconsContainer>
    ) : (
      <StyledIconPlaceholder />
    );

  const shouldLeftAlignTitle = () =>
    actions.length > 1 || (leadingIcon && actions.length === 1);

  const renderContent = () => {
    if (variant === AppBarVariant.Primary) {
      return (
        <StyledProductHeader>
          <StyledSideSection>{renderLeadingIcon()}</StyledSideSection>
          <StyledTitle
            variant="textHeadlineMediumOnSurface"
            onClick={onTitleClick}
            isLeftAligned={!!shouldLeftAlignTitle()}
            data-cy={dataCy}
            {...otherProps}
          >
            {title}
          </StyledTitle>
          <StyledSideSection>
            {actionComponent || renderActions()}
          </StyledSideSection>
        </StyledProductHeader>
      );
    }

    if (variant === AppBarVariant.StackedBack) {
      return (
        <StyledStackedLayout>
          <StyledBack>
            <StyledBackIconContainer>
              {renderLeadingIcon()}
            </StyledBackIconContainer>
            {renderActions()}
          </StyledBack>
          <StyledStackedBackTypography
            variant="textHeadlineMediumOnSurface"
            onClick={onTitleClick}
            data-cy={dataCy}
          >
            {title}
          </StyledStackedBackTypography>
          {actionComponent}
        </StyledStackedLayout>
      );
    }
  };

  return <StyledAppBarContainer>{renderContent()}</StyledAppBarContainer>;
};

const StyledAppBarContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: var(--rk-spacing-xxxxxl);
  background-color: var(--md-sys-color-surface-container-low);
`;

const StyledProductHeader = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(var(--rk-spacing-xxxxxl), auto) 1fr minmax(
      var(--rk-spacing-xxxxxl),
      auto
    );
  align-items: center;
`;

const StyledSideSection = styled.div`
  display: flex;
  align-items: center;
  min-width: var(--rk-spacing-xxxxxl);
`;

const StyledIconPlaceholder = styled.div`
  width: var(--rk-spacing-xxxxxl);
  height: var(--rk-spacing-xxxxxl);
`;

const StyledActionIconsContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-sm);
  align-items: center;
  justify-content: flex-end;
`;

interface StyledTitleProps {
  isLeftAligned: boolean;
}

const StyledTitle = styled(Typography)<StyledTitleProps>`
  text-align: ${(props) => (props.isLeftAligned ? 'left' : 'center')};
`;

const StyledStackedLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledBack = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledBackIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledStackedBackTypography = styled(Typography)`
  margin-left: var(--rk-spacing-md);
  cursor: pointer;
`;
